import React, { FC } from 'react'
import { useIntl } from 'react-intl'

import { makeStyles } from '@material-ui/core'

import { playerProfilePalette } from 'lib'

type Props = {
  totalPlayerPerformance: number
  totalTeamBonus: number
  totalPenalties: number
}

const useStyles = makeStyles(({ typography, palette }) => {
  return {
    svg: {
      transform: 'rotateZ(-90deg)',
    },
    svgGreenProgress: {
      transform: 'rotateZ(8.5deg)',
      transformOrigin: 'center',
    },
    svgGreyProgress: {
      transform: 'rotateZ(-8.5deg)',
      transformOrigin: 'center',
    },
    svgPointsValue: {
      fontWeight: typography.fontWeightBold,
      fontSize: 36,
      fill: palette.common.white,
      transform: 'rotateZ(90deg)',
      transformOrigin: 'center',
    },
    svgPointsLabel: {
      fontWeight: typography.fontWeightMedium,
      fontSize: 12,
      fill: '#8EA5C2',
      opacity: .5,
      transform: 'rotateZ(90deg)',
      transformOrigin: 'center',
    },
  }
})

const PointspreadGraphDrawing: FC<Props> = ({
  totalPlayerPerformance,
  totalTeamBonus,
  totalPenalties,
}) => {
  const classes = useStyles()
  const { formatMessage } = useIntl()

  const totalGain = totalPlayerPerformance + totalTeamBonus
  const totalPoints = totalGain + totalPenalties

  const redStrokeWidth = 7
  const redRadius = 146 / 2 - redStrokeWidth / 2
  const redCircumference = 2 * Math.PI * redRadius
  const redProgress = Math.min(1, totalGain !== 0 ? -totalPenalties / totalGain : -totalPenalties)
  const redProgressOffset = (redProgress - 1) * redCircumference

  const greenStrokeWidth = 10
  const greenRadius = 111 / 2 - greenStrokeWidth / 2
  const greenCircumference = 2 * Math.PI * greenRadius
  const greenProgress = totalGain !== 0 ? totalPlayerPerformance / totalGain : 0
  const greenProgressOffset = (1 - greenProgress) * greenCircumference

  const greyStrokeWidth = 10
  const greyRadius = 111 / 2 - greyStrokeWidth / 2
  const greyCircumference = 2 * Math.PI * greyRadius
  const greyProgress = totalGain !== 0 ? totalTeamBonus / totalGain : 0
  const greyProgressOffset = (greyProgress - 1) * greyCircumference

  const center = 73
  const gap = totalPlayerPerformance > 0 && totalTeamBonus > 0 ? 15 : 0

  return (
    <svg
      className={classes.svg}
      viewBox="0 0 146 146"
    >
      <defs>
        <linearGradient
          id="PointspreadGraph__greyGradient"
          x1="100%"
          y1="0%"
          x2="100%"
          y2="100%"
        >
          <stop
            offset="0%"
            stopColor={playerProfilePalette.teamBonus[0]}
          />
          <stop
            offset="100%"
            stopColor={playerProfilePalette.teamBonus[1]}
          />
        </linearGradient>

        <linearGradient
          id="PointspreadGraph__redGradient"
          x1="100%"
          y1="0%"
          x2="100%"
          y2="100%"
        >
          <stop
            offset="0%"
            stopColor={playerProfilePalette.penalties[0]}
          />
          <stop
            offset="100%"
            stopColor={playerProfilePalette.penalties[1]}
          />
        </linearGradient>

        <linearGradient
          id="PointspreadGraph__greenGradient"
          x1="100%"
          y1="0%"
          x2="100%"
          y2="100%"
        >
          <stop
            offset="0%"
            stopColor={playerProfilePalette.playerPerformance[0]}
          />
          <stop
            offset="100%"
            stopColor={playerProfilePalette.playerPerformance[1]}
          />
        </linearGradient>
      </defs>

      <circle
        fill="none"
        stroke="#8EA5C2"
        opacity={.18}
        cx={center}
        cy={center}
        r={redRadius}
        strokeWidth={3}
      />

      {redProgress > 0 && (
        <circle
          fill="none"
          stroke="url(#PointspreadGraph__redGradient)"
          strokeLinecap="round"
          cx={center}
          cy={center}
          r={redRadius}
          strokeWidth={redStrokeWidth}
          strokeDasharray={redCircumference}
          strokeDashoffset={redProgressOffset}
        />
      )}

      {greenProgress > 0 && (
        <circle
          className={classes.svgGreenProgress}
          fill="none"
          stroke="url(#PointspreadGraph__greenGradient)"
          strokeLinecap="round"
          cx={center}
          cy={center}
          r={greenRadius}
          strokeWidth={greenStrokeWidth}
          strokeDasharray={greenCircumference}
          strokeDashoffset={greenProgressOffset + gap}
        />
      )}

      {greyProgress > 0 && (
        <circle
          className={classes.svgGreyProgress}
          fill="none"
          stroke="url(#PointspreadGraph__greyGradient)"
          strokeLinecap="round"
          cx={center}
          cy={center}
          r={greyRadius}
          strokeWidth={greyStrokeWidth}
          strokeDasharray={greyCircumference}
          strokeDashoffset={greyProgressOffset - gap}
        />
      )}

      <text
        x={center}
        y={center + 8}
        textAnchor="middle"
        className={classes.svgPointsValue}
      >
        {totalPoints}
      </text>

      <text
        x={center}
        y={center + 24}
        textAnchor="middle"
        className={classes.svgPointsLabel}
      >
        {formatMessage({ id: 'playerprofile_pointspread_points' })}
      </text>
    </svg>
  )
}

export default PointspreadGraphDrawing
try {
    // @ts-ignore
    PointspreadGraphDrawing.displayName = "PointspreadGraphDrawing";
    // @ts-ignore
    PointspreadGraphDrawing.__docgenInfo = { "description": "", "displayName": "PointspreadGraphDrawing", "props": { "totalPlayerPerformance": { "defaultValue": null, "description": "", "name": "totalPlayerPerformance", "required": true, "type": { "name": "number" } }, "totalTeamBonus": { "defaultValue": null, "description": "", "name": "totalTeamBonus", "required": true, "type": { "name": "number" } }, "totalPenalties": { "defaultValue": null, "description": "", "name": "totalPenalties", "required": true, "type": { "name": "number" } } } };
    // @ts-ignore
    if (typeof STORYBOOK_REACT_CLASSES !== "undefined")
        // @ts-ignore
        STORYBOOK_REACT_CLASSES["src/components/PlayerLayer/components/PlayerProfile/tabs/PointspreadTab/components/PointspreadGraph/components/PointspreadGraphDrawing.tsx#PointspreadGraphDrawing"] = { docgenInfo: PointspreadGraphDrawing.__docgenInfo, name: "PointspreadGraphDrawing", path: "src/components/PlayerLayer/components/PlayerProfile/tabs/PointspreadTab/components/PointspreadGraph/components/PointspreadGraphDrawing.tsx#PointspreadGraphDrawing" };
}
catch (__react_docgen_typescript_loader_error) { }